import React, { useState } from 'react';
import Button from '../UI/Button';
import FileUpload from '../UI/FileUpload';
import { useAppSelector } from '../../hooks/redux';
import { api } from '../../services/api';

interface ProjectFile {
  id?: string;
  _id?: string;
  type: 'image' | 'video' | 'document';
  filename: string;
  originalFilename: string;
  url: string;
  thumbnailUrl?: string;
  mimeType: string;
  size: number;
  description?: string;
  synopsis?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  updatedAt?: string;
  updatedBy?: string;
  currentVersion?: number;
  versions?: Array<{
    version: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
    description?: string;
    synopsis?: string;
    changeNotes?: string;
  }>;
}

interface ProjectFilesListProps {
  files: ProjectFile[];
  projectId?: string;
  isOwner?: boolean;
  onFilesUpdate?: () => void;
}

const ProjectFilesList: React.FC<ProjectFilesListProps> = ({ files, projectId, isOwner = false, onFilesUpdate }) => {
  const [showUpload, setShowUpload] = useState(false);
  const { accessToken } = useAppSelector((state) => state.auth);

  const handleFilesUploaded = async (uploadedFiles: any[]) => {
    // Files are automatically uploaded by FileUpload component
    // Now create ProjectFile records for each uploaded file
    if (projectId && accessToken) {
      try {
        for (const file of uploadedFiles) {
          await api.projectFiles.createFile(
            projectId,
            {
              type: file.type,
              filename: file.filename,
              originalFilename: file.originalFilename,
              url: file.url,
              thumbnailUrl: file.thumbnailUrl,
              mimeType: file.mimeType,
              size: file.size,
              description: file.description,
              synopsis: file.synopsis,
            },
            accessToken
          );
        }
        // Refresh files list
        if (onFilesUpdate) {
          onFilesUpdate();
        }
      } catch (error) {
        console.error('Error creating project files:', error);
      }
    }
    setShowUpload(false);
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter out deleted files for non-owners
  const visibleFiles = isOwner ? files : files.filter((f: any) => !f.deletedAt);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Project Files</h3>
        {isOwner && !showUpload && (
          <Button
            type="button"
            size="sm"
            onClick={() => setShowUpload(true)}
          >
            Upload Files
          </Button>
        )}
      </div>

      {showUpload && isOwner && projectId && (
        <div className="border border-[var(--color-neutral-border)] rounded-lg p-4 bg-[var(--color-neutral-50)] mb-4">
          <FileUpload
            onFileSelect={handleFilesUploaded}
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip"
            maxSize={104857600}
            autoUpload={true}
            projectId={projectId}
            allowDocumentMetadata={true}
          />
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowUpload(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {visibleFiles.length === 0 ? (
        <div className="text-center py-8 text-[var(--color-subtext-color)]">
          <p>No project files available.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleFiles.map((file) => {
          const fileId = file.id || file._id || '';
          const hasRevisions = file.versions && file.versions.length > 1;

          return (
            <div key={fileId} className="border border-[var(--color-neutral-border)] rounded-lg p-4 bg-[var(--color-default-background)] hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-3">
                {file.type === 'image' && file.thumbnailUrl ? (
                  <img
                    src={file.thumbnailUrl}
                    alt={file.originalFilename}
                    className="w-16 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-16 h-16 bg-[var(--color-neutral-100)] rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-[var(--color-neutral-400)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[var(--color-default-font)] truncate">{file.originalFilename}</h4>
                  <div className="text-xs text-[var(--color-subtext-color)] mt-1 space-y-1">
                    <div>{file.type} • {formatFileSize(file.size)}</div>
                    <div>Added: {formatDate(file.uploadedAt)}</div>
                    {file.updatedAt && (
                      <div className="text-[var(--color-brand-primary)]">
                        Updated: {formatDate(file.updatedAt)}
                        {hasRevisions && ` (v${file.currentVersion || 1})`}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {file.description && (
                <div className="text-sm text-[var(--color-default-font)] mb-2">
                  <strong>Description:</strong> {file.description}
                </div>
              )}

              {file.type === 'document' && file.synopsis && (
                <div className="text-sm text-[var(--color-default-font)] mb-2">
                  <strong>Synopsis:</strong> {file.synopsis}
                </div>
              )}

              {hasRevisions && (
                <div className="mt-2 text-xs text-[var(--color-brand-primary)]">
                  {file.versions!.length} version{file.versions!.length > 1 ? 's' : ''} available
                </div>
              )}

              <div className="mt-3">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--color-brand-primary)] hover:text-[var(--color-brand-700)]"
                >
                  View File →
                </a>
              </div>
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};

export default ProjectFilesList;

